import React from "react";
import { Link } from "react-router-dom";
import { Milk, ShoppingBag, Truck, Shield, ChevronRight, Sparkles, BarChart3, Globe2 } from "lucide-react";
import { Button } from "../components/ui/button";

const features = [
  { icon: Milk, title: "Recurring Subscriptions", desc: "Daily, alternate, custom — never run out of milk." },
  { icon: Truck, title: "Doorstep Delivery", desc: "Morning & evening slots. Live tracking on the day." },
  { icon: ShoppingBag, title: "Wallet First", desc: "Recharge once, auto-debit on delivery. No haggling." },
  { icon: Shield, title: "Bottle Return", desc: "Track every bottle. Damaged & lost reports built-in." },
  { icon: BarChart3, title: "Operator Console", desc: "Routes, riders, reports — for owners to run lean." },
  { icon: Globe2, title: "White-label SaaS", desc: "Your brand, your colors, your dairy. We power it." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-brand-cream text-brand-ink page-enter" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-brand-cream/90 backdrop-blur border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <div className="w-9 h-9 rounded-2xl bg-brand-sky flex items-center justify-center">
              <Milk className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-heading font-extrabold text-xl tracking-tight">DairyFresh</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <a href="#features" className="text-neutral-700 hover:text-brand-sky">Features</a>
            <a href="#pricing" className="text-neutral-700 hover:text-brand-sky">Pricing</a>
            <Link to="/admin/login" className="text-neutral-700 hover:text-brand-sky" data-testid="admin-login-link">Tenant Admin</Link>
            <Link to="/super-admin/login" className="text-neutral-700 hover:text-brand-sky" data-testid="super-admin-login-link">Super Admin</Link>
          </nav>
          <Link to="/login" data-testid="get-started-btn">
            <Button className="bg-brand-sky hover:bg-blue-700 rounded-2xl h-11 px-5 font-bold">
              Customer App <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 pt-16 pb-20 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-brand-leaf/15 rounded-full text-brand-leaf text-xs font-bold uppercase tracking-wider mb-6">
            <Sparkles className="w-3.5 h-3.5" /> Multi-tenant SaaS · Built for Bharat
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight mb-6">
            Run your <span className="text-brand-sky">dairy</span><br/>like a delight.
          </h1>
          <p className="text-base sm:text-lg text-neutral-700 max-w-lg leading-relaxed mb-8">
            DairyFresh is the operating system for milk subscription businesses — wallet, subscriptions, routes, bottle returns and live tracking — packaged into one delightful PWA for rural & semi-urban India.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/login" data-testid="hero-customer-cta">
              <Button className="bg-brand-sky hover:bg-blue-700 rounded-2xl h-14 px-7 font-bold text-base">
                Try Customer App
              </Button>
            </Link>
            <Link to="/admin/login" data-testid="hero-admin-cta">
              <Button variant="outline" className="rounded-2xl h-14 px-7 font-bold text-base border-2 border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white">
                Run a Dairy
              </Button>
            </Link>
          </div>
          <div className="mt-10 flex items-center gap-6 text-sm text-neutral-600">
            <div><b className="text-brand-ink text-lg block font-heading">2,400+</b> subscribers</div>
            <div className="w-px h-10 bg-neutral-300" />
            <div><b className="text-brand-ink text-lg block font-heading">98%</b> on-time delivery</div>
            <div className="w-px h-10 bg-neutral-300" />
            <div><b className="text-brand-ink text-lg block font-heading">5</b> languages</div>
          </div>
        </div>
        <div className="relative">
          <div className="absolute -inset-6 bg-gradient-to-br from-brand-sky/20 via-brand-leaf/10 to-brand-clay/10 blur-3xl rounded-[3rem]" />
          <img
            src="https://images.unsplash.com/photo-1639151082235-406d8eb262b9?w=900&q=85"
            alt="Fresh milk"
            className="relative rounded-[2.5rem] shadow-xl object-cover w-full h-[480px]"
            data-testid="hero-image"
          />
          <div className="absolute -bottom-6 -left-6 bg-white rounded-3xl p-5 shadow-xl border border-neutral-200 w-64">
            <div className="text-xs uppercase tracking-wider text-neutral-500 font-bold">Tomorrow's Delivery</div>
            <div className="font-heading text-2xl font-bold mt-1">5:30 AM</div>
            <div className="text-sm text-neutral-700 mt-1">Toned Milk 500ml × 1</div>
            <div className="mt-3 flex items-center gap-2 text-brand-leaf text-xs font-bold">
              <span className="w-2 h-2 rounded-full bg-brand-leaf animate-pulse" /> Out for delivery soon
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="bg-white border-y border-neutral-200 py-20">
        <div className="max-w-6xl mx-auto px-5">
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-wider font-bold text-brand-sky mb-3">The complete stack</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Everything a dairy needs.<br/>Nothing it doesn't.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map(({ icon: Icon, title, desc }, i) => (
              <div key={i} className="p-7 rounded-3xl border border-neutral-200 bg-brand-cream card-hover" data-testid={`feature-card-${i}`}>
                <div className="w-12 h-12 rounded-2xl bg-brand-sky/10 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-brand-sky" strokeWidth={2} />
                </div>
                <h3 className="font-heading font-bold text-xl mb-2">{title}</h3>
                <p className="text-neutral-700 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="pricing" className="py-20 max-w-6xl mx-auto px-5">
        <div className="rounded-[2.5rem] bg-brand-ink text-white p-10 sm:p-16 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-80 h-80 bg-brand-sky/30 blur-3xl rounded-full" />
          <div className="relative max-w-2xl">
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">Launch your dairy SaaS in a day.</h2>
            <p className="text-white/70 text-lg mb-8">From Starter (₹2,999/mo) to Enterprise (₹19,999/mo). Add modules à la carte — AI insights, reels, WhatsApp, route optimization, and more.</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/admin/login" data-testid="cta-admin-btn">
                <Button className="bg-white text-brand-ink hover:bg-white/90 rounded-2xl h-14 px-7 font-bold text-base">Tenant Admin Login</Button>
              </Link>
              <Link to="/super-admin/login" data-testid="cta-super-btn">
                <Button variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 rounded-2xl h-14 px-7 font-bold text-base">Super Admin</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-neutral-200 py-10 text-center text-sm text-neutral-600">
        DairyFresh © 2026 · A multi-tenant milk delivery SaaS · Built for India 🇮🇳
      </footer>
    </div>
  );
}
